// ---------------------------------------------------------------------------
// Local sync bookkeeping: the bridge between the SQLite tables and the sync
// engine. Everything here is generic over entity type so repository.ts never
// had to change — writes are captured by the triggers in schema.ts, and this
// module drains that outbox, serializes rows to JSON, and applies remote
// changes back (with triggers suppressed so applies don't echo).
// ---------------------------------------------------------------------------

import { getDb } from '../db/database';
import { notifyDbChanged } from '../db/events';
import { SYNCABLE_TABLES } from '../db/schema';
import { newId, nowIso } from '../utils/id';
import type { Database as SqlJsDatabase, SqlValue } from 'sql.js';

// DB seam: sync normally runs against the app's singleton database, but tests
// inject a specific Database so two "devices" can be simulated in one process.
let injectedDb: SqlJsDatabase | null = null;
export function _setSyncDb(db: SqlJsDatabase | null): void {
  injectedDb = db;
}
export function syncDb(): SqlJsDatabase {
  return injectedDb ?? getDb();
}

type Row = Record<string, SqlValue>;

function q(sql: string, params: SqlValue[] = []): Row[] {
  const stmt = syncDb().prepare(sql);
  try {
    stmt.bind(params);
    const rows: Row[] = [];
    while (stmt.step()) rows.push(stmt.getAsObject());
    return rows;
  } finally {
    stmt.free();
  }
}
function q1(sql: string, params: SqlValue[] = []): Row | null {
  const rows = q(sql, params);
  return rows.length ? rows[0] : null;
}
function run(sql: string, params: SqlValue[] = []): void {
  syncDb().run(sql, params);
}

// Settings keys that are tied to THIS machine and must never sync.
export const NON_PORTABLE_SETTINGS = [
  'codexPath',
  'defaultModel',
  'workingDirectory',
  'microphoneId',
  'ttsVoice',
  'onboardedAt',
  'aiProvider',
];

const DOC_TABLES = new Set(SYNCABLE_TABLES.filter((t) => t.kind === 'doc').map((t) => t.table));
const BLOB_TABLES = new Set(SYNCABLE_TABLES.filter((t) => t.kind === 'blob').map((t) => t.table));

/** Single-column pk for a doc table (all doc tables have one). */
const DOC_PK: Record<string, string> = {
  app_settings: 'id',
  student_profile: 'id',
  dental_program: 'id',
  interview_question: 'id',
  saved_answer: 'id',
  saved_answer_version: 'id',
  practice_session: 'id',
  practice_attempt: 'id',
  mock_interview: 'id',
  improvement_plan: 'id',
  review_state: 'question_id',
  program_dossier: 'program_id',
  red_flag_plan: 'profile_id',
  prompt_template: 'id',
};

export function isDocEntity(type: string): boolean {
  return DOC_TABLES.has(type);
}
export function isBlobEntity(type: string): boolean {
  return BLOB_TABLES.has(type);
}

// --- sync_control (trigger gate) -------------------------------------------

function getControl(key: string): string {
  const row = q1('SELECT value FROM sync_control WHERE key = ?', [key]);
  return row ? String(row.value) : '0';
}
function setControl(key: string, value: string): void {
  run('UPDATE sync_control SET value = ? WHERE key = ?', [value, key]);
}

export function isSyncEnabled(): boolean {
  return getControl('enabled') === '1';
}
export function setSyncEnabled(on: boolean): void {
  setControl('enabled', on ? '1' : '0');
}

/**
 * Run a mutation with the outbox triggers suppressed — used when APPLYING remote
 * changes so they aren't re-queued as local changes (which would echo forever).
 */
export function withSuppressedTriggers<T>(fn: () => T): T {
  setControl('suppress', '1');
  try {
    return fn();
  } finally {
    setControl('suppress', '0');
  }
}

// --- sync_meta (device id, account, cursor) --------------------------------

export function getMeta(key: string): string | null {
  const row = q1('SELECT value FROM sync_meta WHERE key = ?', [key]);
  return row ? String(row.value) : null;
}
export function setMeta(key: string, value: string | null): void {
  if (value === null) {
    run('DELETE FROM sync_meta WHERE key = ?', [key]);
  } else {
    run('INSERT OR REPLACE INTO sync_meta (key, value) VALUES (?, ?)', [key, value]);
  }
}

/** Stable per-install device id, generated once. */
export function getDeviceId(): string {
  let id = getMeta('device_id');
  if (!id) {
    id = newId('device');
    setMeta('device_id', id);
  }
  return id;
}

export function getCursor(): number {
  return Number(getMeta('cursor') ?? '0') || 0;
}
export function setCursor(n: number): void {
  setMeta('cursor', String(n));
}

export function getCloudUser(): { id: string; email: string | null } | null {
  const id = getMeta('cloud_user_id');
  if (!id) return null;
  return { id, email: getMeta('cloud_email') };
}
export function setCloudUser(id: string | null, email: string | null): void {
  setMeta('cloud_user_id', id);
  setMeta('cloud_email', email);
}

/**
 * True when this device holds meaningful user-created prep data (used to decide
 * whether hosted-web sign-in can adopt the cloud copy without asking). Settings
 * and prompt templates alone don't count — they exist on every fresh install.
 */
export function hasLocalUserData(): boolean {
  const tables = [
    'student_profile',
    'dental_program',
    'interview_question',
    'saved_answer',
    'practice_attempt',
    'mock_interview',
  ];
  for (const t of tables) {
    const row = q1(`SELECT COUNT(*) AS c FROM ${t}`);
    if (Number(row?.c ?? 0) > 0) return true;
  }
  return false;
}

// --- sync_doc_state (per-row server revision) ------------------------------

export function getDocRevision(type: string, id: string): number {
  const row = q1(
    'SELECT server_revision FROM sync_doc_state WHERE entity_type = ? AND entity_id = ?',
    [type, id],
  );
  return row ? Number(row.server_revision) : 0;
}
export function setDocRevision(type: string, id: string, rev: number): void {
  run(
    `INSERT INTO sync_doc_state (entity_type, entity_id, server_revision) VALUES (?, ?, ?)
     ON CONFLICT(entity_type, entity_id) DO UPDATE SET server_revision = excluded.server_revision`,
    [type, id, rev],
  );
}

// --- Outbox ----------------------------------------------------------------

export interface OutboxChange {
  entityType: string;
  entityId: string;
  op: 'upsert' | 'delete';
}

/**
 * Drain the outbox up to the current max seq, coalescing repeated edits of the
 * same row into a single change (latest op wins). Returns the max seq so the
 * caller can clear exactly what it read even if new writes arrive meanwhile.
 */
export function readOutbox(): { maxSeq: number; changes: OutboxChange[] } {
  const rows = q('SELECT seq, entity_type, entity_id, op FROM sync_outbox ORDER BY seq ASC');
  let maxSeq = 0;
  const latest = new Map<string, OutboxChange>();
  for (const r of rows) {
    maxSeq = Number(r.seq);
    const entityType = String(r.entity_type);
    const entityId = String(r.entity_id);
    latest.set(`${entityType} ${entityId}`, {
      entityType,
      entityId,
      op: String(r.op) === 'delete' ? 'delete' : 'upsert',
    });
  }
  return { maxSeq, changes: [...latest.values()] };
}

export function clearOutboxThrough(maxSeq: number): void {
  run('DELETE FROM sync_outbox WHERE seq <= ?', [maxSeq]);
}

export function outboxCount(): number {
  const row = q1('SELECT COUNT(*) AS c FROM sync_outbox');
  return Number(row?.c ?? 0);
}

// --- Row serialization (doc tables) ----------------------------------------

/** Portable subset of app_settings as a JSON string (drops machine-local keys). */
function serializeSettings(row: Record<string, SqlValue>): string {
  let data: Record<string, unknown> = {};
  try {
    data = JSON.parse(String(row.data ?? '{}'));
  } catch {
    data = {};
  }
  for (const key of NON_PORTABLE_SETTINGS) delete data[key];
  return JSON.stringify(data);
}

/** JSON snapshot of a doc row, or null if it no longer exists. */
export function serializeDoc(type: string, id: string): string | null {
  const pk = DOC_PK[type];
  if (!pk) return null;
  const row = q1(`SELECT * FROM ${type} WHERE ${pk} = ?`, [id]);
  if (!row) return null;
  if (type === 'app_settings') return serializeSettings(row as Record<string, SqlValue>);
  return JSON.stringify(row);
}

/**
 * Columns that actually exist on a table, cached per table name. Cloud payloads
 * are JSON whose keys become SQL column names in applyDocUpsert; restricting
 * them to this allowlist means a malicious/corrupt payload can neither inject
 * SQL through a key nor error out the apply loop with an unknown column.
 */
const TABLE_COLUMNS = new Map<string, Set<string>>();
function tableColumns(table: string): Set<string> {
  let cols = TABLE_COLUMNS.get(table);
  if (!cols) {
    cols = new Set(q(`PRAGMA table_info(${table})`).map((r) => String(r.name)));
    TABLE_COLUMNS.set(table, cols);
  }
  return cols;
}

/**
 * Apply an upserted doc row from the cloud. Uses ON CONFLICT DO UPDATE (never
 * INSERT OR REPLACE) so replacing a parent row can't cascade-delete its
 * children (e.g. saved_answer_version rows under a saved_answer).
 */
export function applyDocUpsert(type: string, payloadJson: string): void {
  const pk = DOC_PK[type];
  if (!pk) return;
  if (type === 'app_settings') {
    applySettingsMerge(payloadJson);
    return;
  }
  let obj: Record<string, unknown>;
  try {
    obj = JSON.parse(payloadJson);
  } catch {
    return;
  }
  const allowed = tableColumns(type);
  const cols = Object.keys(obj).filter((c) => allowed.has(c));
  if (cols.length === 0 || !cols.includes(pk)) return;
  const placeholders = cols.map(() => '?').join(', ');
  const updates = cols.filter((c) => c !== pk).map((c) => `${c} = excluded.${c}`).join(', ');
  const values = cols.map((c) => obj[c] as SqlValue);
  const sql =
    `INSERT INTO ${type} (${cols.join(', ')}) VALUES (${placeholders}) ` +
    (updates ? `ON CONFLICT(${pk}) DO UPDATE SET ${updates}` : `ON CONFLICT(${pk}) DO NOTHING`);
  run(sql, values);
}

/** Merge only the portable keys from a cloud settings payload into local settings. */
function applySettingsMerge(payloadJson: string): void {
  let incoming: Record<string, unknown> = {};
  try {
    incoming = JSON.parse(payloadJson);
  } catch {
    return;
  }
  for (const key of NON_PORTABLE_SETTINGS) delete incoming[key];
  const row = q1('SELECT data FROM app_settings WHERE id = 1');
  let current: Record<string, unknown> = {};
  if (row) {
    try {
      current = JSON.parse(String(row.data));
    } catch {
      current = {};
    }
  }
  const merged = { ...current, ...incoming };
  run('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
    JSON.stringify(merged),
  ]);
}

export function applyDocDelete(type: string, id: string): void {
  const pk = DOC_PK[type];
  if (!pk) return;
  run(`DELETE FROM ${type} WHERE ${pk} = ?`, [id]);
}

// --- Local conflict mirror (for the resolution UI) -------------------------

export interface LocalConflict {
  id: string;
  entityType: string;
  entityId: string;
  baseRevision: number;
  serverRevision: number;
  serverPayload: string | null;
  clientPayload: string | null;
  createdAt: string;
}

export function upsertLocalConflict(c: LocalConflict): void {
  run(
    `INSERT INTO sync_conflict (id, entity_type, entity_id, base_revision, server_revision, server_payload, client_payload, created_at, resolved)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, 0)
     ON CONFLICT(id) DO UPDATE SET server_revision = excluded.server_revision,
       server_payload = excluded.server_payload, client_payload = excluded.client_payload, resolved = 0`,
    [c.id, c.entityType, c.entityId, c.baseRevision, c.serverRevision, c.serverPayload, c.clientPayload, c.createdAt],
  );
}

export function listLocalConflicts(): LocalConflict[] {
  return q('SELECT * FROM sync_conflict WHERE resolved = 0 ORDER BY created_at ASC').map((r) => ({
    id: String(r.id),
    entityType: String(r.entity_type),
    entityId: String(r.entity_id),
    baseRevision: Number(r.base_revision),
    serverRevision: Number(r.server_revision),
    serverPayload: r.server_payload == null ? null : String(r.server_payload),
    clientPayload: r.client_payload == null ? null : String(r.client_payload),
    createdAt: String(r.created_at),
  }));
}

export function countLocalConflicts(): number {
  const row = q1('SELECT COUNT(*) AS c FROM sync_conflict WHERE resolved = 0');
  return Number(row?.c ?? 0);
}

export function markConflictResolved(id: string): void {
  run('UPDATE sync_conflict SET resolved = 1 WHERE id = ?', [id]);
}

/** Notify screens that applied remote changes should refresh the UI. */
export function notifyApplied(): void {
  notifyDbChanged();
}

export { nowIso };
